import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next, anodum, view, dialogs } from '@yola/ws-sdk';
import { Panel, PanelGroup, designSystem, reactHookForm } from '@yola/ws-ui';
import isIframeEmbed from '../helpers/is-iframe-embed';
import getIframeCode from '../helpers/get-iframe-code';
import formatUrl from '../helpers/format-url';
import copyAttributes from '../helpers/copy-attributes';
import ModalDialog from './modal-dialog';
import LinkForm from './link-form';
import CodeForm from './code-form';
import getTagName from '../helpers/get-tag-name';
import tabIds from '../constants/tab-ids';
import fieldNames from '../constants/field-names';

const { EMBED_CODE, EMBED_URL } = tabIds;
const {
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
  DialogHeader,
  DialogHeaderTitle,
  TabsList,
  Tab,
  TabsContentList,
  TabContent,
  Tabs,
} = designSystem;
const { useForm } = reactHookForm;

const getCaptions = () => ({
  title: i18next.t('Iframe settings'),
  embedUrl: i18next.t('Embed URL'),
  embedCode: i18next.t('Embed Code'),
  submit: i18next.t('Submit'),
  cancel: i18next.t('Cancel'),
});

const IframeEditDialog = ({ elementId, onDialogMainAction, onDialogCancel }) => {
  const [currentTab, setCurrentTab] = useState(isIframeEmbed(elementId) ? EMBED_CODE : EMBED_URL);

  const captions = useMemo(getCaptions, []);
  const defaultValues = useMemo(
    () => ({
      [fieldNames.URL_VALUE]: view.accessors.getElementAttribute(elementId, 'src'),
      [fieldNames.IS_FULLSCREEN_ALLOWED]: view.verifiers.hasElementAttribute(
        elementId,
        'allowfullscreen'
      ),
      [fieldNames.EMBED_CODE]: getIframeCode(elementId),
    }),
    [elementId]
  );

  const { control, formState, getValues } = useForm({
    defaultValues,
    mode: 'onTouched',
  });
  const { isValid, errors } = formState;

  const applyLinkSettings = useCallback(
    (values) => {
      const { urlValue, isFullscreenAllowed } = values;
      const doc = view.accessors.getLiveDocument();
      const iframeTagName = getTagName();
      const newIframeElement = doc.createElement(iframeTagName);

      newIframeElement.setAttribute('src', formatUrl(urlValue, elementId));

      if (isFullscreenAllowed) {
        newIframeElement.setAttribute('allowfullscreen', '');
      }

      view.operations.replaceElement(newIframeElement, elementId);

      dialogs.operations.hide();
    },
    [elementId]
  );

  const applyCodeSettings = useCallback(
    (values) => {
      const iframeTagName = getTagName();
      const parsedEmbedCode = anodum.parseElementFromString(values.embedCode);
      let newIframeElement;

      if (iframeTagName === 'ws-iframe') {
        newIframeElement = document.createElement('ws-iframe');

        copyAttributes(newIframeElement, parsedEmbedCode);
      } else {
        newIframeElement = parsedEmbedCode;
      }

      view.operations.replaceElement(newIframeElement, elementId);

      dialogs.operations.hide();
    },
    [elementId]
  );

  const onSubmit = useCallback(() => {
    if (!isValid) {
      const tabsWithError = Object.keys(errors).reduce((acc, fieldName) => {
        if (fieldName === fieldNames.URL_VALUE) {
          acc.push(EMBED_URL);
        }
        if (fieldName === fieldNames.EMBED_CODE) {
          acc.push(EMBED_CODE);
        }

        return acc;
      }, []);

      if (tabsWithError.length && !tabsWithError.includes(currentTab)) {
        setCurrentTab(tabsWithError[0]);
      }
      return;
    }

    const values = getValues();
    onDialogMainAction();

    if (currentTab === EMBED_URL) {
      applyLinkSettings(values);
    }

    if (currentTab === EMBED_CODE) {
      applyCodeSettings(values);
    }
  }, [
    applyCodeSettings,
    applyLinkSettings,
    currentTab,
    onDialogMainAction,
    getValues,
    isValid,
    errors,
  ]);

  const onCancel = useCallback(() => {
    onDialogCancel();
    dialogs.operations.hide();
  }, [onDialogCancel]);

  return (
    <ModalDialog targetElementId={elementId} onClose={onCancel} onSubmit={onSubmit}>
      <PanelGroup height="100%">
        <Panel
          align="middle"
          corners="squared"
          className="ws-drag-trigger"
          style={{ flexShrink: '0', borderBottom: 'none' }}
        >
          <DialogHeader>
            <DialogHeaderTitle>{captions.title}</DialogHeaderTitle>
          </DialogHeader>
        </Panel>
        <Panel height="100%" scrollable style={{ borderBottom: 'none' }}>
          <Tabs
            disableSwipeableContent
            skipRenderInactiveContent
            activeTabId={currentTab}
            onTabChange={setCurrentTab}
          >
            <TabsList variant="fullWidth">
              <Tab tabId={EMBED_URL} label={captions.embedUrl} />
              <Tab tabId={EMBED_CODE} label={captions.embedCode} />
            </TabsList>
            <TabsContentList>
              <TabContent tabId={EMBED_URL}>
                <LinkForm control={control} />
              </TabContent>
              <TabContent tabId={EMBED_CODE}>
                <CodeForm control={control} />
              </TabContent>
            </TabsContentList>
          </Tabs>
        </Panel>
        <Panel corners="squared" style={{ flexShrink: '0' }}>
          <DialogButtonGroup>
            <DialogButton onClick={onSubmit}>{captions.submit}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onCancel}>{captions.cancel}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </ModalDialog>
  );
};

IframeEditDialog.propTypes = {
  elementId: PropTypes.string.isRequired,
  // Passed from ws-editor's `dialogs-container`:
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default IframeEditDialog;
